import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { CrashlyticsService } from './crashlytics.service';
import { FirebaseService } from './firebase.service';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';

export interface GTMEvent {
  event: string;
  [key: string]: unknown;
}

export type AnalyticsEventType =
  | 'loginEvent'
  | 'logoutEvent'
  | 'sessionTimeOut'
  | 'sessionContinueEvent'
  | 'sessionExpired'
  | 'pageView'
  | 'buttonClick'
  | 'formSubmit'
  | 'error'
  | 'paymentInitiated'
  | 'paymentCompleted'
  | 'paymentFailed'
  | 'autopayEnrolled'
  | 'autopayUnenrolled'
  | 'autopayEdited'
  | 'autopaySetupStarted'
  | 'autopaySetupStep'
  | 'autopaySetupAbandoned'
  | 'autopaySetupFailed'
  | 'autopayEligibilityCheck'
  | 'tcpaConsentView'
  | 'tcpaConsentGiven'
  | 'tcpaConsentDeferred'
  | 'tcpaEconsentAccepted'
  | 'tcpaEconsentDeclined'
  | 'tcpaConsentFlowCompleted'
  | 'profileSettingsView'
  | 'profileEditStarted'
  | 'profileSaved'
  | 'phoneAuthorizationChange'
  | 'copyToAllLoans'
  | 'paperlessSettingsView'
  | 'paperlessSettingsSaved'
  | 'paperlessEnrolled'
  | 'paperlessUnenrolled'
  | 'documentDownload'
  | 'messageRead'
  | 'messageSent'
  | 'Co-Browse Requested'
  | 'PDF Co-Browse Started'
  | 'mobileNavTabSwitch'
  | 'mobileHeaderButtonClick'
  | 'nativeMenuOpened'
  | 'nativeMenuItemClick'
  | 'mobileBorrowerSwitched'
  | 'mobileTourStarted'
  | 'mobileMfaPreferencesOpened'
  | 'mobileBackNavigation'
  | 'mobileMessagesOpened';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly crashlyticsService = inject(CrashlyticsService);
  private readonly platformService = inject(PlatformService);

  private get gtmId(): string {
    return this.env.appSettings.gtmId || '';
  }

  private get isEnabled(): boolean {
    return !!this.gtmId;
  }

  private get dataLayer(): GTMEvent[] {
    const win = window as Window & { dataLayer?: GTMEvent[] };
    if (!win.dataLayer) {
      win.dataLayer = [];
    }
    return win.dataLayer;
  }

  initialize(): void {
    if (!this.isEnabled) {
      this.loggingService.logWarning('Analytics: GTM ID not configured, analytics disabled');
      return;
    }

    if (document.querySelector(`script[src*="googletagmanager.com/gtm.js"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.async = false;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;

    this.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    document.head.appendChild(script);
  }

  pushEvent(event: GTMEvent): void {
    const platform = this.platformService.platform();
    const deviceDetails = this.platformService.deviceDetails();

    const enrichedEvent: GTMEvent = {
      ...event,
      appPlatform: platform,
      appVersion: deviceDetails?.appVersion,
    };

    if (this.isEnabled) {
      this.dataLayer.push(enrichedEvent);
    }

    this.forwardToFirebase(enrichedEvent);
  }

  private forwardToFirebase(event: GTMEvent): void {
    const { event: eventName, ...params } = event;
    if (typeof eventName !== 'string' || eventName.startsWith('gtm.') || eventName === 'userId') {
      return;
    }

    const normalizedName = this.normalizeFirebaseEventName(eventName);
    if (!normalizedName) {
      return;
    }

    try {
      const firebaseParams: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
          firebaseParams[key] = value;
        }
      }
      this.firebaseService.logEvent(normalizedName, firebaseParams);
    } catch {
      this.loggingService.logWarning(`Analytics: Failed to forward event '${eventName}' to Firebase`);
    }
  }

  private normalizeFirebaseEventName(name: string): string | null {
    const normalized = name.replace(/[\s-]+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    if (!normalized || !/^[a-zA-Z]/.test(normalized)) {
      return null;
    }
    return normalized.slice(0, 40);
  }

  pushTagtoDataLayer(event: GTMEvent): void {
    this.pushEvent(event);
  }

  trackEvent(eventName: string, data?: Record<string, unknown>): void {
    this.pushEvent({
      event: eventName,
      ...data,
    });
  }

  trackSessionTimeout(): void {
    this.pushEvent({
      event: 'sessionTimeOut',
      sessionStatus: 'Time Out',
    });
  }

  trackSessionContinue(): void {
    this.pushEvent({
      event: 'sessionContinueEvent',
      sessionStatus: 'Continue',
    });
  }

  trackSessionExpired(): void {
    this.pushEvent({
      event: 'sessionExpired',
      sessionStatus: 'Expired',
    });
  }

  trackLogin(): void {
    this.pushEvent({
      event: 'loginEvent',
      loginStatus: 'login',
    });
  }

  trackLogout(): void {
    this.pushEvent({
      event: 'logoutEvent',
      loginStatus: 'logout',
    });
  }

  trackPageView(pageName: string, pageUrl?: string): void {
    this.pushEvent({
      event: 'pageView',
      pageName,
      pageUrl: pageUrl || window.location.pathname,
    });
  }

  trackPaymentInitiated(amount: number): void {
    this.pushEvent({
      event: 'paymentInitiated',
      paymentAmount: amount,
    });
  }

  trackPaymentCompleted(amount: number, confirmationNumber?: string): void {
    this.pushEvent({
      event: 'paymentCompleted',
      paymentAmount: amount,
      confirmationNumber,
    });
  }

  trackPaymentFailed(errorMessage: string): void {
    this.pushEvent({
      event: 'paymentFailed',
      errorMessage,
    });
  }

  trackAutopayEnrolled(
    details?: {
      frequency?: 'monthly' | 'biweekly' | 'semimonthly';
      amount?: number;
      additionalPrincipal?: number;
      additionalEscrow?: number;
    }
  ): void {
    this.pushEvent({
      event: 'autopayEnrolled',
      autopayFrequency: details?.frequency,
      autopayAmount: details?.amount,
      additionalPrincipal: details?.additionalPrincipal,
      additionalEscrow: details?.additionalEscrow,
    });
  }

  trackAutopayUnenrolled(reason?: string): void {
    this.pushEvent({
      event: 'autopayUnenrolled',
      cancellationReason: reason,
    });
  }

  trackAutopayEdited(
    changes?: {
      frequencyChanged?: boolean;
      bankAccountChanged?: boolean;
      amountChanged?: boolean;
    }
  ): void {
    this.pushEvent({
      event: 'autopayEdited',
      ...changes,
    });
  }

  trackAutopaySetupStarted(source?: string): void {
    this.pushEvent({
      event: 'autopaySetupStarted',
      setupSource: source,
    });
  }

  trackAutopaySetupStep(step: string): void {
    this.pushEvent({
      event: 'autopaySetupStep',
      setupStep: step,
    });
  }

  trackAutopaySetupAbandoned(lastStep: string): void {
    this.pushEvent({
      event: 'autopaySetupAbandoned',
      abandonedAtStep: lastStep,
    });
  }

  trackAutopaySetupFailed(errorCode: string, errorMessage?: string): void {
    this.pushEvent({
      event: 'autopaySetupFailed',
      errorCode,
      errorMessage,
    });
  }

  trackAutopayEligibilityCheck(
    isEligible: boolean,
    eligibilityType?: 'inhouse' | 'repay' | 'none',
    reason?: string
  ): void {
    this.pushEvent({
      event: 'autopayEligibilityCheck',
      isEligible,
      eligibilityType,
      ineligibilityReason: !isEligible ? reason : undefined,
    });
  }

  trackPaperlessEnrolled(): void {
    this.pushEvent({
      event: 'paperlessEnrolled',
    });
  }

  trackPaperlessUnenrolled(): void {
    this.pushEvent({
      event: 'paperlessUnenrolled',
    });
  }

  trackDocumentDownload(documentType: string): void {
    this.pushEvent({
      event: 'documentDownload',
      documentType,
    });
  }

  trackMessageRead(messageId: string): void {
    this.pushEvent({
      event: 'messageRead',
      messageId,
    });
  }

  trackMessageSent(category?: string, topic?: string): void {
    this.pushEvent({
      event: 'messageSent',
      messageCategory: category,
      messageTopic: topic,
    });
  }

  trackSecureMessageInboxLoaded(messageCount: number, unreadCount: number): void {
    this.pushEvent({
      event: 'secure-msg-inbox-loaded',
      totalMessages: messageCount,
      unreadMessages: unreadCount,
    });
  }

  trackSecureMessageInboxRefresh(): void {
    this.pushEvent({
      event: 'secure-msg-inbox-refresh',
    });
  }

  trackSecureMessageComposeOpened(isReply = false): void {
    this.pushEvent({
      event: 'secure-msg-inbox-open_compose',
      isReply,
    });
  }

  trackSecureMessageComposeLoaded(): void {
    this.pushEvent({
      event: 'secure-msg-compose-loaded',
    });
  }

  trackSecureMessageCategorySelected(category: string): void {
    this.pushEvent({
      event: 'secure-msg-opt1',
      selectedCategory: category,
    });
  }

  trackSecureMessageTopicSelected(category: string, topic: string): void {
    this.pushEvent({
      event: 'secure-msg-opt2',
      selectedCategory: category,
      selectedTopic: topic,
    });
  }

  trackSecureMessageSubmit(category: string, topic: string, hasAttachments: boolean): void {
    this.pushEvent({
      event: 'secure-msg-submit',
      messageCategory: category,
      messageTopic: topic,
      hasAttachments,
    });
  }

  trackSecureMessageDeleted(isInbox: boolean): void {
    this.pushEvent({
      event: 'secure-msg-inbox-deleted',
      messageType: isInbox ? 'inbox' : 'sent',
    });
  }

  trackSecureMessageAttachmentDownload(filename: string): void {
    this.pushEvent({
      event: 'secure-msg-download-attachment',
      filename,
    });
  }

  trackSecureMessageReplyOpened(caseId: string): void {
    this.pushEvent({
      event: 'secure-msg-inbox-reply-open',
      caseId,
    });
  }

  trackSecureMessageReplySent(caseId: string): void {
    this.pushEvent({
      event: 'secure-msg-inbox-reply-send',
      caseId,
    });
  }

  trackError(errorType: string, errorMessage: string, errorSource?: string): void {
    this.pushEvent({
      event: 'error',
      errorType,
      errorMessage,
      errorSource,
    });
  }

  trackButtonClick(buttonName: string, category?: string): void {
    this.pushEvent({
      event: 'buttonClick',
      buttonName,
      category,
    });
  }

  trackFormSubmit(formName: string, success: boolean): void {
    this.pushEvent({
      event: 'formSubmit',
      formName,
      success,
    });
  }

  trackProfileSettingsView(): void {
    this.pushEvent({
      event: 'profileSettingsView',
    });
  }

  trackProfileEditStarted(): void {
    this.pushEvent({
      event: 'profileEditStarted',
    });
  }

  trackProfileSaved(
    changes: {
      emailChanged?: boolean;
      phoneChanged?: boolean;
      addressChanged?: boolean;
    }
  ): void {
    this.pushEvent({
      event: 'profileSaved',
      ...changes,
    });
  }

  trackPhoneAuthorizationChange(authorized: boolean): void {
    this.pushEvent({
      event: 'phoneAuthorizationChange',
      authorized,
    });
  }

  trackCopyToAllLoans(targetLoanCount: number): void {
    this.pushEvent({
      event: 'copyToAllLoans',
      targetLoanCount,
    });
  }

  trackTcpaConsentView(loanIndex: number, totalLoans: number): void {
    this.pushEvent({
      event: 'tcpaConsentView',
      loanIndex,
      totalLoans,
    });
  }

  trackTcpaConsentGiven(): void {
    this.pushEvent({
      event: 'tcpaConsentGiven',
    });
  }

  trackTcpaConsentDeferred(): void {
    this.pushEvent({
      event: 'tcpaConsentDeferred',
    });
  }

  trackTcpaEconsentAccepted(): void {
    this.pushEvent({
      event: 'tcpaEconsentAccepted',
    });
  }

  trackTcpaEconsentDeclined(): void {
    this.pushEvent({
      event: 'tcpaEconsentDeclined',
    });
  }

  trackTcpaConsentFlowCompleted(totalLoans: number, consentsGiven: number): void {
    this.pushEvent({
      event: 'tcpaConsentFlowCompleted',
      totalLoans,
      consentsGiven,
    });
  }

  trackPaperlessSettingsView(): void {
    this.pushEvent({
      event: 'paperlessSettingsView',
    });
  }

  trackPaperlessSettingsSaved(
    settings: {
      isTaxPaperless?: boolean;
      isBillingPaperless?: boolean;
      isEscrowPaperless?: boolean;
      isOtherLettersPaperless?: boolean;
    }
  ): void {
    this.pushEvent({
      event: 'paperlessSettingsSaved',
      ...settings,
    });
  }

  trackCoBrowseRequested(): void {
    this.pushEvent({
      event: 'Co-Browse Requested',
      timestamp: new Date().toISOString(),
    });
  }

  trackPdfCoBrowse(documentUrl: string): void {
    this.pushEvent({
      event: 'PDF Co-Browse Started',
      documentUrl,
      timestamp: new Date().toISOString(),
    });
  }

  setUserId(userId: string): void {
    this.pushEvent({
      event: 'userId',
      userId,
    });

    this.firebaseService.setUserId(userId);
    this.firebaseService.setUserProperties({
      app_platform: this.platformService.platform(),
    });

    this.crashlyticsService.setUserId(userId);
  }

  trackLoanLinkFormSubmit(): void {
    this.pushEvent({
      event: 'linkLoanFormSubmit',
    });
  }

  trackLoanLinkSuccess(): void {
    this.pushEvent({
      event: 'loanLinkSuccess',
    });
  }

  trackLoanLinkFailed(errorMessage: string): void {
    this.pushEvent({
      event: 'loanLinkFailed',
      errorMessage,
    });
  }

  trackHelocPopupOpen(): void {
    this.pushEvent({
      event: 'HelocPopupOpen',
    });
  }

  trackHelocPopupContinue(): void {
    this.pushEvent({
      event: 'HelocPopupContinue',
    });
  }

  trackHelocPopupDismiss(): void {
    this.pushEvent({
      event: 'HelocPopupDismiss',
    });
  }

  trackHelocPopupRemindLater(): void {
    this.pushEvent({
      event: 'HelocPopupRemindme',
    });
  }

  trackMakePaymentPageView(): void {
    this.pushEvent({
      event: 'makePaymentInhouse',
    });
  }

  trackAddBankAccount(): void {
    this.pushEvent({
      event: 'addBankAccount',
    });
  }

  trackManageAccountsView(): void {
    this.pushEvent({
      event: 'manageAccountsView',
    });
  }

  trackMobileNavTabSwitch(tabId: string): void {
    this.pushEvent({
      event: 'mobileNavTabSwitch',
      tabId,
    });
  }

  trackMobileHeaderButtonClick(button: string): void {
    this.pushEvent({
      event: 'mobileHeaderButtonClick',
      button,
    });
  }

  trackNativeMenuOpened(sheet: string): void {
    this.pushEvent({
      event: 'nativeMenuOpened',
      sheet,
    });
  }

  trackNativeMenuItemClick(sheet: string, itemId: string): void {
    this.pushEvent({
      event: 'nativeMenuItemClick',
      sheet,
      itemId,
    });
  }

  trackMobileBorrowerSwitched(borrowerId: string): void {
    this.pushEvent({
      event: 'mobileBorrowerSwitched',
      borrowerId,
    });
  }

  trackMobileTourStarted(): void {
    this.pushEvent({
      event: 'mobileTourStarted',
    });
  }

  trackMobileMfaPreferencesOpened(): void {
    this.pushEvent({
      event: 'mobileMfaPreferencesOpened',
    });
  }

  trackMobileBackNavigation(from: string): void {
    this.pushEvent({
      event: 'mobileBackNavigation',
      from,
    });
  }

  trackMobileMessagesOpened(): void {
    this.pushEvent({
      event: 'mobileMessagesOpened',
    });
  }
}
